/* What goes into what, what it costs, and what breaks if you change it.
 *
 * The three questions people actually ask of a bill of materials, which
 * ERPNext answers in three different places: a tree view, a child table
 * nobody finds, and a report. Here they are tabs on the document.
 *
 * Every figure is ERPNext's own. Nothing on this screen adds anything up --
 * a BOM screen with its own arithmetic is a second set of books, and the
 * first time it disagrees with the ledger nobody knows which one is wrong.
 */
import { useCallback, useEffect, useState } from 'react'
import type { ErpError } from './api'
import { useErp } from './context'
import { formatCurrency, formatNumber } from './format'
import { Failed, Loading } from '@jackdesk/shell'
import type { BomCost, BomExploded, BomNode, BomUse } from './types'

type Tab = 'tree' | 'exploded' | 'cost' | 'where'

export function BomView({ name, item, onOpenBom, onOpenItem }: {
  name: string
  /** What this BOM makes, for the "what else uses it" question. */
  item?: string
  onOpenBom?: (bom: string) => void
  onOpenItem?: (item: string) => void
}) {
  const [tab, setTab] = useState<Tab>('tree')
  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row" role="tablist" aria-label="How to look at this bill of materials"
           style={{ gap: 4, flexWrap: 'wrap' }}>
        {([
          ['tree', 'What goes into it'],
          ['exploded', 'Every raw material'],
          ['cost', 'What it costs'],
          ['where', 'What else uses it'],
        ] as [Tab, string][]).map(([which, label]) => (
          <button key={which} type="button" role="tab" className="btn btn-sm"
                  aria-selected={tab === which} data-on={tab === which || undefined}
                  onClick={() => setTab(which)}>{label}</button>
        ))}
      </div>
      {tab === 'tree' ? <BomTree name={name} onOpenBom={onOpenBom} onOpenItem={onOpenItem} /> : null}
      {tab === 'exploded' ? <BomExplodedList name={name} onOpenItem={onOpenItem} /> : null}
      {tab === 'cost' ? <BomCostPanel name={name} /> : null}
      {tab === 'where' ? <WhereUsed item={item ?? ''} onOpenBom={onOpenBom} /> : null}
    </div>
  )
}

/* ── the tree ────────────────────────────────────────────────────────────
 * One level at a time. A deep BOM is thousands of rows and nobody reads a
 * thousand rows; the ones that go deeper say so, and open when asked.
 */
function BomTree({ name, onOpenBom, onOpenItem }: {
  name: string
  onOpenBom?: (bom: string) => void
  onOpenItem?: (item: string) => void
}) {
  return (
    <div className="card card-pad">
      <Level bom={name} depth={0} onOpenBom={onOpenBom} onOpenItem={onOpenItem} />
    </div>
  )
}

function Level({ bom, depth, onOpenBom, onOpenItem }: {
  bom: string
  depth: number
  onOpenBom?: (bom: string) => void
  onOpenItem?: (item: string) => void
}) {
  const erp = useErp()
  const [rows, setRows] = useState<BomNode[] | null>(null)
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [failed, setFailed] = useState<ErpError | null>(null)

  useEffect(() => {
    let cancelled = false
    erp.bomTree(bom)
      .then((r) => { if (!cancelled) setRows(r.children) })
      .catch((e) => { if (!cancelled) setFailed(e as ErpError) })
    return () => { cancelled = true }
  }, [erp, bom])

  if (failed) return <Failed message={failed.message} requestId={failed.requestId} onRetry={() => setFailed(null)} />
  if (!rows) return <Loading rows={3} label="Reading the bill of materials" />
  if (!rows.length) return <p className="small muted" style={{ margin: 0 }}>Nothing goes into this one.</p>

  return (
    <ul className="bom-level" style={{ marginLeft: depth ? 18 : 0 }}>
      {rows.map((r) => (
        <li key={r.item_code + (r.bom_no ?? '')}>
          <div className="bom-row">
            {r.expandable && r.bom_no ? (
              <button type="button" className="bom-twist"
                      aria-expanded={!!open[r.item_code]}
                      aria-label={`${open[r.item_code] ? 'Hide' : 'Show'} what goes into ${r.item_name || r.item_code}`}
                      onClick={() => setOpen((o) => ({ ...o, [r.item_code]: !o[r.item_code] }))}>
                {open[r.item_code] ? '▾' : '▸'}
              </button>
            ) : <span className="bom-twist" aria-hidden="true" />}

            <button type="button" className="linkish" onClick={() => onOpenItem?.(r.item_code)}>
              {r.item_name || r.item_code}
            </button>
            <span className="small muted">{r.item_code}</span>
            <span className="bom-qty num">{formatNumber(r.stock_qty)} {r.uom ?? ''}</span>
            {r.phantom ? <span className="chip">not stocked</span> : null}
            {r.subcontracted ? <span className="chip">made outside</span> : null}
            {r.bom_no ? (
              <button type="button" className="linkish small" onClick={() => onOpenBom?.(r.bom_no!)}>
                {r.bom_no}
              </button>
            ) : null}
          </div>
          {open[r.item_code] && r.bom_no ? (
            <Level bom={r.bom_no} depth={depth + 1} onOpenBom={onOpenBom} onOpenItem={onOpenItem} />
          ) : null}
        </li>
      ))}
    </ul>
  )
}

/* ── everything at the bottom ─────────────────────────────────────────── */
function BomExplodedList({ name, onOpenItem }: {
  name: string
  onOpenItem?: (item: string) => void
}) {
  const erp = useErp()
  const [data, setData] = useState<BomExploded | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const load = useCallback(() => {
    setFailed(null)
    erp.bomExploded(name).then(setData).catch((e) => setFailed(e as ErpError))
  }, [erp, name])
  useEffect(load, [load])

  if (failed) return <Failed message={failed.message} requestId={failed.requestId} onRetry={load} />
  if (!data) return <Loading rows={4} label="Working out the raw materials" />

  return (
    <div className="card">
      <p className="small muted" style={{ padding: '10px 12px 0', margin: 0 }}>
        Every raw material to make {formatNumber(data.quantity)} of {data.item}, with every
        sub-assembly already broken down. These are ERPNext's own figures — the same ones
        the costing uses.
      </p>
      <table className="tbl">
        <thead>
          <tr><th>Part</th><th className="num">Quantity</th><th className="num">Rate</th><th className="num">Cost</th></tr>
        </thead>
        <tbody>
          {data.items.map((r) => (
            <tr key={r.item_code} onClick={() => onOpenItem?.(r.item_code)}>
              <td>{r.item_name || r.item_code}<span className="small muted"> · {r.item_code}</span></td>
              <td className="num">{formatNumber(r.qty)} {r.uom ?? ''}</td>
              <td className="num">{r.rate == null ? '' : formatCurrency(r.rate)}</td>
              <td className="num">{r.amount == null ? '' : formatCurrency(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ── what it costs ───────────────────────────────────────────────────── */
function BomCostPanel({ name }: { name: string }) {
  const erp = useErp()
  const [data, setData] = useState<BomCost | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const load = useCallback(() => {
    setFailed(null)
    erp.bomCost(name).then(setData).catch((e) => setFailed(e as ErpError))
  }, [erp, name])
  useEffect(load, [load])

  if (failed) return <Failed message={failed.message} requestId={failed.requestId} onRetry={load} />
  if (!data) return <Loading rows={3} label="Adding it up" />

  const money = (v?: number | null) =>
    v == null ? '—' : formatCurrency(v, data.currency ?? 'INR')

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="erp-cards">
        {([
          ['Materials', data.raw_material_cost, 'What the parts cost'],
          ['Making it', data.operating_cost, 'Time on the machines'],
          ['Less scrap', data.scrap_material_cost, 'Recovered from offcuts'],
          ['All in', data.total_cost, `For ${formatNumber(data.quantity)} ${data.item}`],
        ] as [string, number | null | undefined, string][]).map(([label, value, hint]) => (
          <div key={label} className="card card-pad">
            <div className="small muted">{label}</div>
            <div style={{ fontSize: 22, fontWeight: 620, marginTop: 2 }}>{money(value)}</div>
            <div className="small muted" style={{ marginTop: 2 }}>{hint}</div>
          </div>
        ))}
      </div>

      <p className="small muted" style={{ margin: 0 }}>
        Materials are priced from <b>{data.priced_from || 'the item'}</b>. A figure that
        looks wrong is usually the wrong basis rather than the wrong sum.
      </p>

      {data.operations.length ? (
        <div className="card">
          <table className="tbl">
            <thead>
              <tr><th>Operation</th><th>Where</th><th className="num">Minutes</th>
                  <th className="num">Per hour</th><th className="num">Cost</th></tr>
            </thead>
            <tbody>
              {data.operations.map((o, i) => (
                <tr key={i} style={{ cursor: 'default' }}>
                  <td>{o.operation}</td>
                  <td>{o.workstation}</td>
                  <td className="num">{formatNumber(o.minutes)}</td>
                  <td className="num">{o.hour_rate == null ? '' : money(o.hour_rate)}</td>
                  <td className="num">{o.cost == null ? '' : money(o.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          No operations on this one, so all of the cost is the parts.
        </p>
      )}
    </div>
  )
}

/* ── what else uses it ───────────────────────────────────────────────── */
function WhereUsed({ item, onOpenBom }: { item: string; onOpenBom?: (bom: string) => void }) {
  const erp = useErp()
  const [rows, setRows] = useState<BomUse[] | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const load = useCallback(() => {
    if (!item) { setRows([]); return }
    setFailed(null)
    erp.bomWhereUsed(item).then((r) => setRows(r.used_in)).catch((e) => setFailed(e as ErpError))
  }, [erp, item])
  useEffect(load, [load])

  if (failed) return <Failed message={failed.message} requestId={failed.requestId} onRetry={load} />
  if (!rows) return <Loading rows={3} label="Looking for what uses it" />
  if (!rows.length) {
    return (
      <div className="card card-pad muted small">
        Nothing else uses {item || 'this'} — changing it affects only this bill of materials.
      </div>
    )
  }

  return (
    <div className="card">
      <p className="small muted" style={{ padding: '10px 12px 0', margin: 0 }}>
        Change the price or the design of {item} and these are what move.
      </p>
      <table className="tbl">
        <thead>
          <tr><th>Goes into</th><th className="num">How many</th><th>Bill of materials</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.bom} onClick={() => onOpenBom?.(r.bom)}>
              <td>{r.makes_name || r.makes}</td>
              <td className="num">{formatNumber(r.qty)} {r.uom ?? ''}</td>
              <td>{r.bom}</td>
              <td>
                {r.default ? <span className="chip chip-ok">the usual one</span>
                  : r.active ? <span className="chip">in use</span>
                  : <span className="chip chip-warn">not in use</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
